import { useCompanyDataServiceV2 } from "@rw1/api-services";
import { CompanyData_V2 } from "@rw1/auto-generated-clients";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { STALE_TIME_MS } from "./constants";

export const COMPANY_DEFAULT_USER_GROUPS_QUERY_KEY = "companyDefaultUserGroups";

interface UseDefaultUserGroupsForACompanyResult {
  defaultUserGroups: CompanyData_V2.DefaultUserGroup[];
  isLoading: boolean;
  error: Error | null;
  refetchDefaultUserGroups: () => void;
}

export const useCompanyDefaultUserGroups = (
  companyId?: string,
  enabled?: boolean,
): UseDefaultUserGroupsForACompanyResult => {
  const companyDataClient = useCompanyDataServiceV2();

  const { data, isLoading, error, refetch } = useQuery<
    CompanyData_V2.DefaultUserGroup[]
  >({
    queryKey: [COMPANY_DEFAULT_USER_GROUPS_QUERY_KEY, companyId],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      return await companyDataClient.getDefaultCompanyUserGroups(companyId);
    },
    enabled: !!enabled && !!companyId,
    staleTime: STALE_TIME_MS,
  });

  const refetchDefaultUserGroups = useCallback(() => {
    void refetch();
  }, [refetch]);

  return useMemo(
    () => ({
      defaultUserGroups: data ?? [],
      isLoading: isLoading || (!!companyId && data === undefined && !error),
      error: error ?? null,
      refetchDefaultUserGroups,
    }),
    [data, isLoading, error, companyId, refetchDefaultUserGroups],
  );
};
